import { create } from "zustand";
import { subscribeWithSelector } from 'zustand/middleware';
import useAuthStore from "./useAuthStore";
import useCinematicStore, { CINEMATIC_NOT_STARTED } from "./useCinematicStore";
import useMetadataStore from "./useMetadataStore";
import useMapStore from "./useMapStore";
import { GameStatus, UnitStatus } from "@utils/enums";
import { EMPTY_ARRAY, CONFIG } from "@utils/constants";

/**
 * Apply incremental effect events to existing effects array
 * Handles applied, removed, and modified effects
 *
 * @param {Array} currentEffects - Current effects array
 * @param {Object} deltaUnit - Delta unit with effect events
 * @returns {Array} - Merged effects array
 */
function applyEffectEvents(currentEffects, deltaUnit) {
  // No effect changes in delta
  if (!deltaUnit.applied_effects && !deltaUnit.removed_effects && !deltaUnit.modified_effects) {
    return currentEffects;
  }

  // Create map for efficient lookups
  const effectsById = new Map(currentEffects.map(e => [e.id, { ...e }]));

  // 1. Apply added effects FIRST (so they exist for modifications)
  if (deltaUnit.applied_effects) {
    deltaUnit.applied_effects.forEach(newEffect => {
      effectsById.set(newEffect.id, newEffect);
    });
  }

  // 2. Apply modified effects (merge changes into existing/newly added effects)
  if (deltaUnit.modified_effects) {
    deltaUnit.modified_effects.forEach(modification => {
      const existing = effectsById.get(modification.id);
      if (existing) {
        // Merge modified fields into existing effect
        effectsById.set(modification.id, {
          ...existing,
          ...modification
        });
      } else {
        console.warn(`[applyEffectEvents] Tried to modify non-existent effect ${modification.id}`);
        console.warn('[applyEffectEvents] modification object:', modification);
      }
    });
  }

  // 3. Apply removed effects LAST
  if (deltaUnit.removed_effects) {
    deltaUnit.removed_effects.forEach(removal => {
      effectsById.delete(removal.id);
    });
  }

  // Convert back to array
  return Array.from(effectsById.values());
}

const useGameStore = create(
  subscribeWithSelector((set, get) => ({
  // --- NEW XHELOS STRUCTURE ---
  _rawGame: null, // The complete xhelos object from backend with all snapshots
  game: null, // The reconstructed game for current snapshot
  currentSnapshotIndex: 'current', // Which snapshot we're viewing ('current', 'base', or number)
  _cinematicUnsubscribe: null, // Store the unsubscribe function for cleanup
  
  // --- UI STATE ---
  currentPlayer: null,
  loading: true,
  error: null,
  isDeployPhase: false,
  isPlanPhase: false,
  isResolutionPhase: false,
  debugKillMode: false,

  // --- ACTIONS ---
  setGame: (normalizedXhelos) => {
    // Early validation
    if (!normalizedXhelos) {
      console.warn('[setGame] No xhelos data provided');
      return;
    }
    
    const state = get();
    const { user: currentUser } = useAuthStore.getState();
    const gameMode = normalizedXhelos.status;
    
    // Determine values based on game mode
    let game, currentSnapshotIndex, isResolutionPhase;
    //console.log('[setGame] Setting game for mode:', gameMode);
    switch(gameMode) {
      case GameStatus.RESOLVED: {
        // Cinematic mode - start from base
        const baseGame = state.getGameByIndex('base', normalizedXhelos);
        game = baseGame;
        currentSnapshotIndex = 'base';
        isResolutionPhase = true;
      
        // Set up cinematic subscription if not exists
        if (!state._cinematicUnsubscribe) {

          // With subscribeWithSelector middleware, we can use the cleaner syntax
          const unsubscribe = useCinematicStore.subscribe(
            (cinematicState) => cinematicState.executedResolutionQueueIndex,
            (eRQI, previousERQI) => {
             /* console.info('📡 [useGameStore] Cinematic subscription triggered:', {
                eRQI,
                previousERQI,
                changed: eRQI !== previousERQI
              });*/
              
              // Guard: Check if we're already at this snapshot
              const currentState = useGameStore.getState();
              const targetSnapshotIndex = eRQI === CINEMATIC_NOT_STARTED ? 'base' : eRQI;
              
              if (currentState.currentSnapshotIndex === targetSnapshotIndex) {
                console.info('⏭️ [useGameStore] Already at snapshot', targetSnapshotIndex, '- skipping update');
                return;
              }
              
              const snapshotIndex = targetSnapshotIndex;
              const reconstructedGame = currentState.getGameByIndex(snapshotIndex);
                     
              // Update game and index
              useGameStore.setState({
                game: reconstructedGame,
                currentSnapshotIndex: snapshotIndex
              });
                    
              /*console.log('[useGameStore] Game updated from cinematic:', {
                eRQI,
                snapshotIndex,
                hasGame: !!reconstructedGame
              });*/
            }
          );
          state._cinematicUnsubscribe = unsubscribe;
          //console.info('✅ [useGameStore] Cinematic subscription established');
        }
        break;
      }
      
      case GameStatus.LOBBY:
      case GameStatus.DEPLOY:
      case GameStatus.PLAN:
      case GameStatus.RESOLVING:  {
         // Planning/deployment mode - use current snapshot
        const currentGame = state.getGameByIndex('current', normalizedXhelos);
        game = currentGame;
        currentSnapshotIndex = 'current';
        isResolutionPhase = false;
        
        // Clean up subscription if exists and reset cinematic state
        if (state._cinematicUnsubscribe) {
          state._cinematicUnsubscribe();
          state._cinematicUnsubscribe = null;
          // Reset cinematic store to index 0 when leaving resolution phase
          useCinematicStore.getState().reset();
          // Clear broken shields visual overlay (hardcode for shield_charges not in state_delta)
          useMapStore.getState().clearBrokenShields();
        }
        break;
      }

      case GameStatus.FINISH: {
        // Planning/deployment mode - use current snapshot
        const currentGame = state.getGameByIndex('finish', normalizedXhelos);
        game = currentGame;
        currentSnapshotIndex = 'finish';
        isResolutionPhase = false;

        // Clean up subscription if exists and reset cinematic state
        if (state._cinematicUnsubscribe) {
          state._cinematicUnsubscribe();
          state._cinematicUnsubscribe = null;
          // Reset cinematic store to index 0 when leaving resolution phase
          useCinematicStore.getState().reset();
          // Clear broken shields visual overlay (hardcode for shield_charges not in state_delta)
          useMapStore.getState().clearBrokenShields();
        }
        break;
      }
      
      default: {
        console.warn('[setGame] Unknown game status:', gameMode);
        // Fallback to current snapshot as safe default
        const currentGame = state.getGameByIndex('current', normalizedXhelos);
        game = currentGame;
        currentSnapshotIndex = 'current';
        isResolutionPhase = false;
        break;
      }
    }
    
    // Calculate other states
    const currentPlayer = normalizedXhelos.players?.find(p => p.user_id === currentUser?.id) || null;
    const isDeployPhase = gameMode === GameStatus.DEPLOY;
    const isPlanPhase = gameMode === GameStatus.PLAN;
    
    // Single set with all values
    set({
      _rawGame: normalizedXhelos,
      game,
      currentSnapshotIndex,
      isResolutionPhase,
      _cinematicUnsubscribe: state._cinematicUnsubscribe,
      currentPlayer,
      isDeployPhase,
      isPlanPhase,
      loading: false,
      error: null
    });
  },

  setLoading: (isLoading) => set({ loading: isLoading }),
  setError: (errorMessage) => set({ error: errorMessage, loading: false }),

  // Clear all game state (used when game is deleted/not found)
  clearGame: () => set({
    game: null,
    _rawGame: null,
    loading: false,
    error: null
  }),

  mergeSnapshots: (step) => {
    const state = get();

    if (!state._rawGame?.snapshots?.base) {
      console.warn('[mergeSnapshots] No base snapshot available in _rawGame');
      console.warn('[mergeSnapshots] _rawGame structure:', state._rawGame);
      return null;
    }

    // Start with base snapshot (deep clone to avoid mutations)
    const merged = JSON.parse(JSON.stringify(state._rawGame.snapshots.base));

    // Track accumulated planned_actions modifications across snapshots
    const accumulatedPlannedActions = {};

    // Apply all deltas up to and including this step (now 0-based)
    for (let i = 0; i <= step; i++) {
      const delta = state._rawGame.snapshots[i];
      if (!delta) continue;

      // Merge dynamic objects (units, structures, destructibles)
      if (delta.dynamic_objects) {
        if (delta.dynamic_objects.units) {
         // console.log(`[mergeSnapshots] Step ${i} - Units before merge:`, JSON.parse(JSON.stringify(merged.dynamic_objects.units)));
         // console.log(`[mergeSnapshots] Step ${i} - Delta units:`, delta.dynamic_objects.units);

          // Create a map for efficient updates
          const unitMap = new Map(merged.dynamic_objects.units.map(u => [u.id, u]));

          // Update or add units from delta
          delta.dynamic_objects.units.forEach(deltaUnit => {
            const existingUnit = unitMap.get(deltaUnit.id);

            // Merge unit properties and apply effect events
            const mergedUnit = existingUnit ? {
              ...existingUnit,
              ...deltaUnit,
              // Apply incremental effect events (applied/removed/modified)
              active_effects: applyEffectEvents(
                existingUnit.active_effects || [],
                deltaUnit
              )
            } : deltaUnit;

            /*console.log(`[mergeSnapshots] Merging unit ${deltaUnit.id}:`, {
              before: existingUnit,
              delta: deltaUnit,
              merged: mergedUnit
            });*/
            unitMap.set(deltaUnit.id, mergedUnit);
          });

          // Convert back to array
          merged.dynamic_objects.units = Array.from(unitMap.values());
          //console.log(`[mergeSnapshots] Step ${i} - Units after merge:`, merged.dynamic_objects.units);
        }
        
        // Same for structures and destructibles if they change
        if (delta.dynamic_objects.structures) {
          merged.dynamic_objects.structures = delta.dynamic_objects.structures;
        }
        if (delta.dynamic_objects.destructibles) {
          merged.dynamic_objects.destructibles = delta.dynamic_objects.destructibles;
        }
      }
      
      // Merge actions if present (future-proofing for action deltas)
      if (delta.actions !== undefined) {
        if (!merged.actions) {
          merged.actions = {};
        }
        
        // Actions are indexed objects, merge by key
        if (typeof delta.actions === 'object' && !Array.isArray(delta.actions)) {
          // Merge action definitions
          merged.actions = { ...merged.actions, ...delta.actions };
        } else if (Object.keys(delta.actions).length === 0) {
          // Empty object means no changes to actions
          // Keep existing actions
        } else {
          // Unexpected format
          console.warn('[mergeSnapshots] Unexpected delta.actions format:', typeof delta.actions);
        }
      }
      
      // Update tile effects if present
      if (delta.tile_effects) {
        merged.tile_effects = { ...merged.tile_effects, ...delta.tile_effects };
      }
      
      // Process planned_actions for this snapshot INSIDE the loop
      if (merged.dynamic_objects?.units && delta) {
        // console.log(`[mergeSnapshots] Step ${i} - Checking snapshot:`, {
        //   hasSnapshot: !!delta,
        //   snapshotType: delta.snapshot_type,
        //   plannedActionId: delta.planned_action_id
        // });
      
        // Check if this action moved the unit
        const rqItem = state._rawGame?.resolution_queue?.[i];
        const unitMoved = rqItem?.unit_moved === true;
        // console.log(`[mergeSnapshots] Step ${i} - ResolutionQueueItem:`, rqItem, 'unitMoved:', unitMoved);
        
        if (delta?.planned_action_id && unitMoved) {
          // Unit moved - update planned action coordinates
          // Get base planned_actions: from previous snapshot if exists, otherwise from raw game
          let basePlannedActions;
          if (i === 0) {
            // First snapshot (after base), use original planned_actions
            basePlannedActions = state._rawGame.team_planned_actions;
            // console.log(`[mergeSnapshots] Step ${i} - Using original planned_actions`);
          } else if (accumulatedPlannedActions[i - 1]) {
            // Use planned_actions from previous snapshot
            basePlannedActions = accumulatedPlannedActions[i - 1];
            // console.log(`[mergeSnapshots] Step ${i} - Using planned_actions from step ${i - 1}`);
          } else {
            // Fallback (should not happen in normal flow)
            basePlannedActions = state._rawGame.team_planned_actions;
            console.warn(`[mergeSnapshots] Step ${i} - No accumulated planned_actions found, using original`);
          }
        
          if (basePlannedActions) {
            // Find the current planned_action being executed
            const currentAction = basePlannedActions.find(a => a.id === delta.planned_action_id);
          
          if (currentAction) {
            // Find the unit's current position after this snapshot
            const unit = merged.dynamic_objects.units.find(u => u.id === currentAction.unit_id);
            
            if (unit) {
                // console.log(`[mergeSnapshots] Processing PlannedAction ID ${delta.planned_action_id} at snapshot ${i}:`);
                /*console.log('  Current action:', {
                  id: currentAction.id,
                  unit_id: currentAction.unit_id,
                  sequence: currentAction.sequence_number,
                  origin_data: currentAction.origin_data,
                  target_data: currentAction.target_data
                });
                console.log('  Unit position after execution:', { x: unit.x, y: unit.y });*/
              
              // Find the NEXT planned_action of the same unit
              const nextAction = basePlannedActions.find(a => 
                a.unit_id === currentAction.unit_id && 
                a.sequence_number === currentAction.sequence_number + 1
              );
              
              // Use a working copy to accumulate changes
              let workingPlannedActions = [...basePlannedActions];
              
                // First update: CURRENT action's TARGET
                workingPlannedActions = workingPlannedActions.map(action => {
                  if (action.id === delta.planned_action_id) {
                  const updatedAction = {
                    ...action,
                    target_data: { x: unit.x, y: unit.y }
                  };
                  
                  // console.log('  UPDATED current action target:', {
                  //   id: updatedAction.id,
                  //   origin_data: updatedAction.origin_data,
                  //   new_target_data: updatedAction.target_data
                  // });
                  
                  return updatedAction;
                }
                return action;
              });
              
              // Second update: NEXT action's ORIGIN (using the already modified workingPlannedActions)
              if (nextAction) {
                workingPlannedActions = workingPlannedActions.map(action => {
                  if (action.id === nextAction.id) {
                    const updatedAction = {
                      ...action,
                      origin_data: { x: unit.x, y: unit.y }
                    };
                    
                    // console.log('  UPDATED next action origin:', {
                    //   id: updatedAction.id,
                    //   new_origin_data: updatedAction.origin_data,
                    //   target_data: updatedAction.target_data
                    // });
                    
                    return updatedAction;
                  }
                  return action;
                });
              } else {
                // console.log('  No next action found (this might be the last action in sequence)');
              }
              
                // Save the accumulated changes for next snapshot
                accumulatedPlannedActions[i] = workingPlannedActions;
                
                // Also save to merged for final result
                merged.team_planned_actions = workingPlannedActions;
              } else {
                console.warn(`[mergeSnapshots] Unit not found for planned_action ${delta.planned_action_id}, unit_id: ${currentAction.unit_id}`);
              }
            } else {
              console.warn(`[mergeSnapshots] PlannedAction not found with ID: ${delta.planned_action_id}`);
            console.warn('  Available planned_action IDs:', basePlannedActions.map(a => a.id));
          }
          } else {
            console.warn('[mergeSnapshots] No basePlannedActions available (neither merged nor rawGame)');
          }
        } else {
          // Unit didn't move OR no planned_action_id - preserve previous planned actions
          // if (delta) {
          //   console.log(`[mergeSnapshots] Step ${i} - No coordinate update needed (unitMoved: ${unitMoved}, planned_action_id: ${delta?.planned_action_id})`);
          // }
          // Preserve planned actions from previous step
          if (i === 0) {
            accumulatedPlannedActions[i] = state._rawGame.team_planned_actions;
          } else {
            accumulatedPlannedActions[i] = accumulatedPlannedActions[i - 1];
          }
        }
      }
    } // End of loop
    
    return merged;
  },
  
  
  // --- NEW GETTERS WITH WARNINGS ---
  
  // Get current units from snapshot
  getCurrentUnits: () => {
    const { game } = get();
    
    if (!game) {
      console.warn('[getCurrentUnits] No game data available!');
      return EMPTY_ARRAY;
    }
    
    const units = game.dynamic_objects?.units;
    if (!units || units.length === 0) {
      //console.warn('[getCurrentUnits] No units found in game!',game);
      //console.trace();
      return EMPTY_ARRAY;
    }
    
    
    return units;
  },
  
  // Get current structures from snapshot
  getCurrentStructures: () => {
    const { game } = get();
    
    if (!game) {
      console.warn('[getCurrentStructures] No game data available!');
      return EMPTY_ARRAY;
    }
    
    const structures = game.dynamic_objects?.structures;
    if (!structures || structures.length === 0) {
      console.warn('[getCurrentStructures] No structures found in game!');
      return EMPTY_ARRAY;
    }
    
    return structures;
  },
  
  // Get planned actions
  getPlannedActions: () => {
    const { game } = get();
    
    if (!game) {
      console.warn('[getPlannedActions] No game data available!');
      //console.trace('[getPlannedActions] Stack trace:');
      return EMPTY_ARRAY;
    }
    
    const actions = game.team_planned_actions;
    if (!actions || actions.length === 0) {
      //console.warn('[getPlannedActions] No planned actions found!');
      //console.trace('[getPlannedActions] Stack trace:');
      return EMPTY_ARRAY;
    }
    
    return actions;
  },
  
  // Get resolution queue item by index
  getResolutionQueueItem: (index) => {
    const { game } = get();
    
    if (!game) {
      console.warn('[getResolutionQueueItem] No game data available!');
      return null;
    }
    
    if (!game.resolution_queue) {
      console.warn('[getResolutionQueueItem] No resolution queue in game!');
      return null;
    }
    
    const item = game.resolution_queue[index];
    if (!item) {
      console.warn(`[getResolutionQueueItem] No item at index ${index}`);
      return null;
    }
    
    return item;
  },
  
  // Get hydrated planned actions (all actions with full data)
  getHydratedPlannedActions: () => {
    const plannedActions = get().getPlannedActions();
    if (plannedActions.length === 0) {
      return EMPTY_ARRAY;
    }
    
    // Hydrate each planned action using getActionById
    const hydrated = plannedActions.map(plannedAction => {
      // Look up the action definition using action_id (not plannedAction.id!)
      // plannedAction.id = PlannedAction record ID (14, 15, etc.)
      // plannedAction.action_id = Action definition ID (1, 4, 5, etc.)
      const actionDefinition = get().getActionById(plannedAction.action_id);
      
      return {
        ...plannedAction,
        // Add the action definition from snapshot if available
        action: actionDefinition || plannedAction.action || null,
        // These are already in plannedAction from team_planned_actions
        sequence_number: plannedAction.sequence_number,
        resolution_order: plannedAction.resolution_order,
      };
    });
    
    // Log summary of hydration
    const hydratedCount = hydrated.filter(a => a.action).length;
    if (hydratedCount < hydrated.length) {
      console.warn(`[getHydratedPlannedActions] Only ${hydratedCount}/${hydrated.length} actions have action definitions`);
    }
    
    return hydrated;
  },
  
  
  // Get unit by ID with warning
  getUnitById: (unitId) => {
    if (!unitId) {
      console.warn('[getUnitById] Called without unitId');
      return null;
    }
    
    const units = get().getCurrentUnits();
    const unit = units.find(u => u.id === unitId);
    
    if (!unit) {
      console.warn(`[getUnitById] Unit with ID ${unitId} not found in current snapshot`);
      console.trace('[getUnitById] Stack trace for debugging:');
      // Log the game variable that getCurrentUnits is using
      const { game } = get();
      console.log('[getUnitById] Current game object:', game);
      console.log('[getUnitById] Available units in game:', units.map(u => ({ id: u.id, name: u.name })));
      console.log('[getUnitById] Looking for unit ID:', unitId);
      return null;
    }
    
    return unit;
  },
  
  // Get deployable unit by ID (for deployment phase)
  getDeployableUnitById: (unitId) => {
    if (!unitId) {
      console.warn('[getDeployableUnitById] Called without unitId');
      return null;
    }
    
    const { game } = get();
    if (!game) {
      console.warn('[getDeployableUnitById] No game data available');
      return null;
    }
    
    if (!game.available_units || game.available_units.length === 0) {
      console.warn('[getDeployableUnitById] No available units in game');
      return null;
    }
    
    const unit = game.available_units.find(u => u.id === unitId);
    
    if (!unit) {
      console.warn(`[getDeployableUnitById] Deployable unit with ID ${unitId} not found`);
      return null;
    }
    
    return unit;
  },
  
  // Get unit from either deployed units or available units
  getUnitOrAvailableUnitById: (unitId) => {
    if (!unitId) {
      console.warn('[getUnitOrAvailableUnitById] Called without unitId');
      return null;
    }

    // First try to get from deployed units
    const units = get().getCurrentUnits();
    let unit = units.find(u => u.id === unitId);

    if (unit) {
      return unit;
    }

    // If not found in deployed units, check available units
    const { game } = get();
    if (game?.available_units && game.available_units.length > 0) {
      unit = game.available_units.find(u => u.id === unitId);
      if (unit) {
        return unit;
      }
    }

    // Unit not found in either place
    console.warn(`[getUnitOrAvailableUnitById] Unit with ID ${unitId} not found in deployed units or available units`);
    return null;
  },

  // Get deployable units (available_units from game)
  getDeployableUnits: () => {
    const { game } = get();
    if (!game) {
      throw new Error('[getDeployableUnits] No game data available');
    }
    // Return empty array if no available_units (valid state when all deployed)
    if (!game.available_units) {
      return [];
    }
    return game.available_units;
  },

  // Check if unit is in deployment (not yet deployed on map)
  isUnitInDeployment: (unitId) => {
    if (!unitId) {
      console.warn('[isUnitInDeployment] Called without unitId');
      return false;
    }

    const unit = get().getUnitOrAvailableUnitById(unitId);

    if (!unit) {
      console.warn(`[isUnitInDeployment] Unit ${unitId} not found`);
      return false;
    }

    return unit.status === UnitStatus.ROSTER;
  },

  // Get unit at specific position in current snapshot
  getUnitAtPosition: (x, y) => {
    if (x === undefined || y === undefined) {
      console.warn('[getUnitAtPosition] Called without x or y coordinates');
      return null;
    }

    const units = get().getCurrentUnits();
    const unit = units.find(u => u.x === x && u.y === y);

    if (!unit) {
      console.log(`[getUnitAtPosition] No unit found at position (${x}, ${y})`);
      return null;
    }

    return unit;
  },
  
  // Get base units - uses appropriate snapshot based on game state
  getBaseUnits: () => {
    const { game } = get();
    
    if (!game) {
      console.warn('[getBaseUnits] No game data available');
      return EMPTY_ARRAY;
    }
    
    // Switch based on game status
    switch (game.status) {
      case GameStatus.DEPLOY:
      case GameStatus.PLAN: {
        // In DEPLOY/PLAN - game is flattened, units are directly in dynamic_objects
        const currentUnits = game.dynamic_objects?.units;
        if (!currentUnits || currentUnits.length === 0) {
          console.warn('[getBaseUnits] No units in dynamic_objects during DEPLOY/PLAN phase');
          return EMPTY_ARRAY;
        }


        return currentUnits;
      }

      case GameStatus.RESOLVING:
      case GameStatus.RESOLVED: {
        // In CINEMATIC/RESOLVED - use base snapshot
        if (!game.snapshots?.base) {
          console.warn('[getBaseUnits] No base snapshot in RESOLVED phase');
          return EMPTY_ARRAY;
        }
        const baseUnits = game.snapshots.base.dynamic_objects?.units;
        if (!baseUnits || baseUnits.length === 0) {
          console.warn('[getBaseUnits] No units in base snapshot');
          return EMPTY_ARRAY;
        }


        return baseUnits;
      }

      case GameStatus.FINISH: {
        // In FINISH - use finish snapshot
        if (!game.snapshots?.finish) {
          console.warn('[getBaseUnits] No finish snapshot in FINISH phase');
          return EMPTY_ARRAY;
        }
        const finishUnits = game.snapshots.finish.dynamic_objects?.units;
        if (!finishUnits || finishUnits.length === 0) {
          console.warn('[getBaseUnits] No units in finish snapshot');
          return EMPTY_ARRAY;
        }

        return finishUnits;
      }

      default: {
        console.warn('[getBaseUnits] Unknown game status:', game.status);
        // Fallback to current snapshot
        const fallbackUnits = game.snapshots?.current?.dynamic_objects?.units;
        return fallbackUnits || EMPTY_ARRAY;
      }
    }
  },
  
  // Get base unit by ID - uses appropriate snapshot based on game state
  getBaseUnitById: (unitId) => {
    if (!unitId) {
      console.warn('[getBaseUnitById] Called without unitId');
      return null;
    }
    
    const units = get().getBaseUnits();
    const unit = units.find(u => u.id === unitId);
    
    if (!unit) {
      console.warn(`[getBaseUnitById] Unit with ID ${unitId} not found`);
      return null;
    }
    
    return unit;
  },
  

  // Get planned action by ID
  getPlannedActionById: (actionId) => {
    if (!actionId) {
      console.warn('[getPlannedActionById] Called without actionId');
      return null;
    }
    
    const actions = get().getPlannedActions();
    const action = actions.find(a => a.id === actionId);
    //console.log("[useGameStore-getPlannedActionById]List of actions:", actions);
    if (!action) {
      console.warn(`[getPlannedActionById] Action with ID ${actionId} not found`);
      console.trace('[getPlannedActionById] Stack trace for debugging:'); 
      return null;
    }
    
    return action;
  },
  
  // Get action definition by ID from current game snapshot
  getActionById: (actionId) => {
    if (!actionId) {
      console.warn('[getActionById] Called without actionId');
      return null;
    }
    
    const { game } = get();
    if (!game) {
      console.warn('[getActionById] No game available');
      return null;
    }
    
    if (!game.actions || Object.keys(game.actions).length === 0) {
      console.warn('[getActionById] No actions in current game snapshot');
      return null;
    }
    
    // Actions are now indexed by ID: {1: {...}, 5: {...}}
    const action = game.actions[actionId];
    if (!action) {
      console.warn(`[getActionById] Action ${actionId} not found in snapshot`);
      return null;
    }
    
    return action;
  },
  
  // Get hydrated planned action by ID (merges team_planned_actions with snapshot action data)
  getHydratedPlannedActionById: (actionId) => {
    if (!actionId) {
      console.warn('[getHydratedPlannedActionById] Called without actionId');
      return null;
    }
    
    // Get the base planned action from team_planned_actions
    const plannedAction = get().getPlannedActionById(actionId);
    if (!plannedAction) {
      return null; // Warning already logged by getPlannedActionById
    }
    
    // Get the full action data from snapshot using action_id (not plannedAction.id!)
    const actionDefinition = get().getActionById(plannedAction.action_id);
    /*console.log(`[getHydratedPlannedActionById] Hydrating planned action ID ${actionId} with action_id ${plannedAction.action_id}:`, {
      hasActionDefinition: !!actionDefinition,
      plannedAction,
      actionDefinition
    });*/
    // Merge the data - add the action definition from snapshot
    const hydratedAction = {
      ...plannedAction,
      // Add the action definition from snapshot if available
      action: actionDefinition || plannedAction.action || null,
      // These are already in plannedAction from backend
      sequence_number: plannedAction.sequence_number,
      resolution_order: plannedAction.resolution_order,
    };
    //console.log('[getHydratedPlannedActionById] Resulting hydrated action:', hydratedAction);
    
    if (!hydratedAction.action) {
      console.warn(`[getHydratedPlannedActionById] No action definition found for planned action ${actionId} (action_id: ${plannedAction.action_id})`);
    }
    
    return hydratedAction;
  },

  // Check current game mode
  getCurrentGameMode: () => {
    const { _rawGame } = get();
    if (!_rawGame) {
      console.warn('[getCurrentGameMode] No game data available');
      return 'no_game';
    }
    
    return _rawGame.status;
  },


  // Get resolution queue for cinematics
  getResolutionQueue: () => {
    const { game } = get();
    
    if (!game) {
      console.warn('[getResolutionQueue] No game data available!');
      return EMPTY_ARRAY;
    }
    
    const queue = game.resolution_queue;
    if (!queue || queue.length === 0) {
      console.warn('[getResolutionQueue] No resolution queue found!');
      return EMPTY_ARRAY;
    }
    
    return queue;
  },
  
  // Private helper to get current snapshot index
  getCurrentSnapshotIndex: () => {
    const state = get();
    const rawGame = state.game;
    
    if (!rawGame) {
      console.warn('[getCurrentSnapshotIndex] No game data available');
      return null;
    }
    
    // During resolution phases, use the currentSnapshotIndex from state
    // For all other phases, always use "current"
    switch (rawGame.status) {
      case GameStatus.RESOLVING:
      case GameStatus.RESOLVED:
        // In resolution mode, return the actual snapshot index from state
        // This could be "base", a number (0+), or "current"
        return state.currentSnapshotIndex;
        
      case GameStatus.LOBBY:
      case GameStatus.DEPLOY:
      case GameStatus.PLAN:
      case GameStatus.FINISH:
        // All non-resolution phases always use current snapshot
        return "finish";
        
      default:
        console.warn('[getCurrentSnapshotIndex] Unknown game status:', rawGame.status);
        return "current";
    }
  },
  
  // Core function that gets game by specific index
  getGameByIndex: (index, optionalRawGame = null) => {
    if (!index && index !== "base" && index !== 0) {
      console.warn('[getGameByIndex] No index provided');
      return null;
    }
    
    // Validate index - 0 is now valid (first action snapshot)
    // "base" = initial state, 0+ = action snapshots
    
    const state = get();
    const rawGame = optionalRawGame || state._rawGame;
    
    if (!rawGame) {
      console.warn('[getGameByIndex] No game data available');
      return null;
    }
    
    // Get the appropriate snapshot
    let snapshot;
    switch (index) {
      case "current":
        snapshot = rawGame.snapshots?.current;
        if (!snapshot) {
          console.warn('[getGameByIndex] No current snapshot available!');
          return null;
        }
        break;

      case "finish":
        /*console.log("rawGame:", rawGame);
        console.trace();*/
        snapshot = rawGame.snapshots?.finish;
        if (!snapshot) {
          console.warn('[getGameByIndex] No current FINISH snapshot available!');
          return null;
        }
        break;        
        
      case "base":
        snapshot = rawGame.snapshots?.base;
        if (!snapshot) {
          console.warn('[getGameByIndex] No base snapshot available!');
          console.trace('[getGameByIndex] Stack trace for missing base snapshot:');
          return null;
        }
        break;
        
      default:
        if (typeof index === 'number' && index >= 0) {
          // Merge base + deltas up to index (0 is now valid)
          snapshot = get().mergeSnapshots(index);
          if (!snapshot) {
            console.warn('[getGameByIndex] Failed to merge snapshots for index:', index);
            return null;
          }
        } else {
          console.warn('[getGameByIndex] Invalid index type:', index, 'Type:', typeof index);
          return null;
        }
    }
    
    // Build reconstructed game
    const reconstructedGame = {
      // Basic game props
      id: rawGame.id,
      name: rawGame.name,
      status: rawGame.status,
      max_players: rawGame.max_players,
      turn_number: rawGame.turn_number,
      winning_team: rawGame.winning_team,
      players: rawGame.players,
      map: rawGame.map,
      team_planned_actions: snapshot.team_planned_actions || rawGame.team_planned_actions,
      resolution_queue: rawGame.resolution_queue,
      available_units: rawGame.available_units,
      actions: snapshot.actions ? snapshot.actions : {},
      dynamic_objects: snapshot.dynamic_objects ? snapshot.dynamic_objects : {},
      static_objects: snapshot.static_objects ? snapshot.static_objects : {},
      interactables: snapshot.interactables ? snapshot.interactables : {},
      tile_effects: snapshot.tile_effects ? snapshot.tile_effects : {},
    };
    
    return reconstructedGame;
  },

  /**
   * Calculate stat analysis for a unit including all modifiers from effects
   * @param {Object} unit - The unit to analyze
   * @returns {Object} Analysis of each stat with base, current, modifiers, and affecting effects
   */
  calculateStatAnalysis: (unit) => {
    if (!unit) {
      console.warn('[calculateStatAnalysis] No unit provided');
      return {};
    }

    // Get metadata for stat constraints
    const metadataState = useMetadataStore.getState();
    
    // Check if metadata is loaded
    if (!metadataState.isReady()) {
      console.error('[calculateStatAnalysis] CRITICAL: Metadata not loaded! Cannot calculate stat analysis properly.');
      console.error('[calculateStatAnalysis] Call useMetadataStore.getState().fetchMetadata() first');
      // Still continue but warn that constraints won't be applied
    }
    
    if (!metadataState.metadata) {
      console.error('[calculateStatAnalysis] No metadata available - stat constraints will NOT be applied!');
    }
    
    // Get tracked stats - with fallback if metadata isn't loaded
    const trackedStats = metadataState.getTrackedStats();
    
    if (!trackedStats || trackedStats.length === 0) {
      console.error('[calculateStatAnalysis] No tracked stats available from metadata!');
      console.error('[calculateStatAnalysis] Using fallback stat list');
    }
    
    const analysis = {};
    
    trackedStats.forEach(stat => {
      // Get base value from template or unit directly
      let base = 0;
      
      // Check unit_template exists
      if (!unit.unit_template) {
        console.warn(`[calculateStatAnalysis] Unit ${unit.id} has no unit_template - using direct properties only`);
        base = unit[stat] ?? 0;
      } else {
        base = unit.unit_template[stat] ?? unit[stat] ?? 0;
      }
      
      let totalModifier = 0;
      const affectingEffects = [];
      
      // Collect all modifiers from active effects
      if (!unit.active_effects) {
        // This is fine - unit might not have any effects
      } else if (!Array.isArray(unit.active_effects)) {
        console.error(`[calculateStatAnalysis] Unit ${unit.id} active_effects is not an array:`, unit.active_effects);
      } else {
        unit.active_effects.forEach(unitEffect => {
          if (!unitEffect) {
            console.error('[calculateStatAnalysis] Null unitEffect in active_effects array');
            return;
          }

          if (!unitEffect.stat_modifiers) {
            // UnitEffect might not modify stats - this is fine
            return;
          }

          if (unitEffect.stat_modifiers[stat] !== undefined) {
            const modValue = unitEffect.stat_modifiers[stat];
            totalModifier += modValue;

            // Check for required unitEffect fields
            if (!unitEffect.id) {
              console.warn('[calculateStatAnalysis] UnitEffect missing ID:', unitEffect);
            }

            if (!unitEffect.effect || !unitEffect.effect.slug) {
              console.warn('[calculateStatAnalysis] UnitEffect missing effect.slug:', unitEffect);
            }

            if (!unitEffect.source) {
              console.warn('[calculateStatAnalysis] UnitEffect missing source information:', unitEffect);
            }

            affectingEffects.push({
              effect_id: unitEffect.id || 'unknown',
              effect_name: unitEffect.effect.slug,
              modifier_value: modValue,
              on_turn_start_counters: unitEffect.effect.is_passive ? null : (unitEffect.variables ? unitEffect.variables.on_turn_start_counters : null),
              is_passive: unitEffect.effect.is_passive || false,
              source: unitEffect.source || { type: 'unknown', name: 'Unknown' }
            });
          }
        });
      }
      
      // Calculate current value with constraints
      let current = base + totalModifier;
      
      // Apply stat constraints from metadata
      if (metadataState.metadata && metadataState.metadata.stat_constraints) {
        const originalValue = current;
        current = metadataState.applyStatConstraint(stat, current);
        
        // Log if constraint was applied
        if (originalValue !== current) {
          console.info(`[calculateStatAnalysis] Constraint applied to ${stat}: ${originalValue} -> ${current}`);
        }
      } else {
        console.warn(`[calculateStatAnalysis] No metadata constraints available for stat: ${stat}`);
      }
      
      analysis[stat] = {
        base,
        current,
        totalModifier,
        isModified: totalModifier !== 0,
        displayClass: totalModifier > 0 ? 'buffed' : totalModifier < 0 ? 'debuffed' : 'normal',
        affecting_effects: affectingEffects
      };
    });
    
    return analysis;
  },

})));

export default useGameStore;